import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import './AdminContentManager.css';

interface Material {
  id: number;
  name: string;
  description: string;
  rarity: string;
  iconName: string;
  imageUrl?: string;
  materialType: string;
  baseValue: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Recipe {
  id: number;
  name: string;
  description: string;
  category: string;
  requiredLevel: number;
  craftingTime: number;
  baseSuccessRate: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  craftedItem?: {
    id: number;
    name: string;
    itemType: string;
    rarity: string;
    iconName: string;
    imageUrl?: string;
  };
  ingredients: Array<{
    materialId: number;
    materialName: string;
    quantity: number;
    rarity: string;
  }>;
}

interface Monster {
  id: number;
  name: string;
  description: string;
  level: number;
  health: number;
  damage: number;
  defense: number;
  speed: number;
  aiPersonality: string;
  monsterType: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  dropTable: Array<{
    materialId: number;
    materialName: string;
    dropRate: number;
    minQuantity: number;
    maxQuantity: number;
  }>;
}

interface CreateMaterialData {
  name: string;
  description: string;
  rarity: string;
  iconName: string;
  materialType: string;
  baseValue: number;
  isActive: boolean;
}

interface CreateRecipeData {
  name: string;
  description: string;
  category: string;
  requiredLevel: number;
  craftingTime: number;
  baseSuccessRate: number;
  craftedItemId: number;
  isActive: boolean;
  ingredients: Array<{
    materialId: number;
    quantity: number;
  }>;
  // Weapon properties
  minDamage?: number;
  maxDamage?: number;
  minRange?: number;
  maxRange?: number;
  damageType?: string;
  aoeShape?: string;
  requiresLoS?: boolean;
  weaponAPCost?: number;
  accuracyModifier?: number;
  usesAmmo?: boolean;
  maxAmmo?: number;
}

interface CreateMonsterData {
  name: string;
  description: string;
  level: number;
  health: number;
  damage: number;
  defense: number;
  speed: number;
  aiPersonality: string;
  monsterType: string;
  isActive: boolean;
}

const AdminContentManager: React.FC = () => {
  const { handleAuthError } = useAuth();
  const [activeTab, setActiveTab] = useState<'materials' | 'recipes' | 'monsters'>('materials');
  const [materials, setMaterials] = useState<Material[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [monsters, setMonsters] = useState<Monster[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [selectedItem, setSelectedItem] = useState<Material | Recipe | Monster | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Form data
  const [materialFormData, setMaterialFormData] = useState<CreateMaterialData>({
    name: '',
    description: '',
    rarity: 'Common',
    iconName: '',
    materialType: 'Wood',
    baseValue: 1,
    isActive: true
  });

  const [recipeFormData, setRecipeFormData] = useState<CreateRecipeData>({
    name: '',
    description: '',
    category: 'Weapon',
    requiredLevel: 1,
    craftingTime: 30,
    baseSuccessRate: 80,
    craftedItemId: 0,
    isActive: true,
    ingredients: [],
    // Weapon defaults
    minDamage: 1,
    maxDamage: 3,
    minRange: 1,
    maxRange: 1,
    damageType: 'Physical',
    aoeShape: 'Single',
    requiresLoS: true,
    weaponAPCost: 1,
    accuracyModifier: 0,
    usesAmmo: false,
    maxAmmo: 0
  });

  const [monsterFormData, setMonsterFormData] = useState<CreateMonsterData>({
    name: '',
    description: '',
    level: 1,
    health: 100,
    damage: 10,
    defense: 5,
    speed: 5,
    aiPersonality: 'Aggressive',
    monsterType: 'Beast',
    isActive: true
  });

  // Fetch materials
  const fetchMaterials = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/materials');
      if (response.ok) {
        const data = await response.json();
        setMaterials(data.materials || []);
      } else {
        setError('Failed to load materials');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error loading materials');
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  // Fetch recipes
  const fetchRecipes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/recipes');
      if (response.ok) {
        const data = await response.json();
        setRecipes(data.recipes || []);
      } else {
        setError('Failed to load recipes');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error loading recipes');
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  // Fetch monsters
  const fetchMonsters = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/monsters');
      if (response.ok) {
        const data = await response.json();
        setMonsters(data.monsters || []);
      } else {
        setError('Failed to load monsters');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error loading monsters');
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (activeTab === 'materials') {
      fetchMaterials();
    } else if (activeTab === 'recipes') {
      fetchRecipes();
    } else if (activeTab === 'monsters') {
      fetchMonsters();
    }
  }, [activeTab, fetchMaterials, fetchRecipes, fetchMonsters]);

  // Create material
  const handleCreateMaterial = async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/materials', {
        method: 'POST',
        body: JSON.stringify(materialFormData)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setMaterialFormData({
          name: '',
          description: '',
          rarity: 'Common',
          iconName: '',
          materialType: 'Wood',
          baseValue: 1,
          isActive: true
        });
        await fetchMaterials();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create material');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error creating material');
    } finally {
      setIsLoading(false);
    }
  };

  // Create recipe
  const handleCreateRecipe = async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/recipes', {
        method: 'POST',
        body: JSON.stringify(recipeFormData)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setRecipeFormData({
          name: '',
          description: '',
          category: 'Weapon',
          requiredLevel: 1,
          craftingTime: 30,
          baseSuccessRate: 80,
          craftedItemId: 0,
          isActive: true,
          ingredients: []
        });
        await fetchRecipes();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create recipe');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error creating recipe');
    } finally {
      setIsLoading(false);
    }
  };

  // Create monster
  const handleCreateMonster = async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/monsters', {
        method: 'POST',
        body: JSON.stringify(monsterFormData)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setMonsterFormData({
          name: '',
          description: '',
          level: 1,
          health: 100,
          damage: 10,
          defense: 5,
          speed: 5,
          aiPersonality: 'Aggressive',
          monsterType: 'Beast',
          isActive: true
        });
        await fetchMonsters();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create monster');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error creating monster');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle item status
  const handleToggleStatus = async (type: string, id: number, currentStatus: boolean) => {
    try {
      const response = await makeAuthenticatedRequest(`/admin/content/${type}/${id}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        if (type === 'materials') await fetchMaterials();
        else if (type === 'recipes') await fetchRecipes();
        else if (type === 'monsters') await fetchMonsters();
        setError(null);
      } else {
        setError(`Failed to update ${type} status`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(`Error updating ${type}`);
    }
  };

  // Delete item
  const handleDeleteItem = async (type: string, id: number) => {
    if (!window.confirm(`Are you sure you want to delete this ${type.slice(0, -1)}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/admin/content/${type}/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        if (type === 'materials') await fetchMaterials();
        else if (type === 'recipes') await fetchRecipes();
        else if (type === 'monsters') await fetchMonsters();
        setError(null);
      } else {
        setError(`Failed to delete ${type.slice(0, -1)}`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(`Error deleting ${type.slice(0, -1)}`);
    }
  };

  const getRarityColor = (rarity: string): string => {
    switch (rarity) {
      case 'Common': return '#95a5a6';
      case 'Uncommon': return '#27ae60';
      case 'Rare': return '#3498db';
      case 'Epic': return '#9b59b6';
      case 'Legendary': return '#f39c12';
      default: return '#95a5a6';
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const openDetailsModal = (item: Material | Recipe | Monster) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  if (isLoading && (materials.length === 0 && recipes.length === 0 && monsters.length === 0)) {
    return (
      <div className="content-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-manager">
      <div className="content-header">
        <div className="content-title">
          <h2>📦 Content Management</h2>
          <p>Manage game materials, recipes, and monsters</p>
        </div>
        <button 
          className="create-content-button"
          onClick={openCreateModal}
        >
          ✨ Create {activeTab === 'materials' ? 'Material' : activeTab === 'recipes' ? 'Recipe' : 'Monster'}
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Content Tabs */}
      <div className="content-tabs">
        <button 
          className={`tab-button ${activeTab === 'materials' ? 'active' : ''}`}
          onClick={() => setActiveTab('materials')}
        >
          🪨 Materials ({materials.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'recipes' ? 'active' : ''}`}
          onClick={() => setActiveTab('recipes')}
        >
          📜 Recipes ({recipes.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'monsters' ? 'active' : ''}`}
          onClick={() => setActiveTab('monsters')}
        >
          👹 Monsters ({monsters.length})
        </button>
      </div>

      {/* Materials Tab */}
      {activeTab === 'materials' && (
        <div className="content-table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>Type</th>
                <th>Rarity</th>
                <th>Value</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {materials.map(material => (
                <tr key={material.id} className="content-row">
                  <td className="content-info">
                    <div className="content-name">{material.name}</div>
                    <div className="content-description">{material.description}</div>
                    <div className="content-icon">🎯 {material.iconName}</div>
                  </td>
                  <td>
                    <span className="type-badge">{material.materialType}</span>
                  </td>
                  <td>
                    <span 
                      className="rarity-badge"
                      style={{ backgroundColor: getRarityColor(material.rarity) }}
                    >
                      {material.rarity}
                    </span>
                  </td>
                  <td className="value-cell">💰 {material.baseValue}</td>
                  <td>
                    <span className={`status-badge ${material.isActive ? 'active' : 'inactive'}`}>
                      {material.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="content-actions">
                    <button 
                      className="view-button"
                      onClick={() => openDetailsModal(material)}
                    >
                      👁️ View
                    </button>
                    <button 
                      className={`toggle-button ${material.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus('materials', material.id, material.isActive)}
                    >
                      {material.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteItem('materials', material.id)}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {materials.length === 0 && (
            <div className="empty-state">
              <h3>No materials found</h3>
              <p>Create your first material to get started!</p>
              <button className="create-content-button" onClick={openCreateModal}>
                ✨ Create Material
              </button>
            </div>
          )}
        </div>
      )}

      {/* Recipes Tab */}
      {activeTab === 'recipes' && (
        <div className="content-table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Recipe</th>
                <th>Category</th>
                <th>Level Req</th>
                <th>Success Rate</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recipes.map(recipe => (
                <tr key={recipe.id} className="content-row">
                  <td className="content-info">
                    <div className="content-name">{recipe.name}</div>
                    <div className="content-description">{recipe.description}</div>
                    <div className="recipe-details">⏱️ {recipe.craftingTime}s • 📦 {recipe.ingredients.length} ingredients</div>
                  </td>
                  <td>
                    <span className="type-badge">{recipe.category}</span>
                  </td>
                  <td className="level-cell">🎯 {recipe.requiredLevel}</td>
                  <td className="success-rate-cell">✅ {recipe.baseSuccessRate}%</td>
                  <td>
                    <span className={`status-badge ${recipe.isActive ? 'active' : 'inactive'}`}>
                      {recipe.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="content-actions">
                    <button 
                      className="view-button"
                      onClick={() => openDetailsModal(recipe)}
                    >
                      👁️ View
                    </button>
                    <button 
                      className={`toggle-button ${recipe.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus('recipes', recipe.id, recipe.isActive)}
                    >
                      {recipe.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteItem('recipes', recipe.id)}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {recipes.length === 0 && (
            <div className="empty-state">
              <h3>No recipes found</h3>
              <p>Create your first recipe to get started!</p>
              <button className="create-content-button" onClick={openCreateModal}>
                ✨ Create Recipe
              </button>
            </div>
          )}
        </div>
      )}

      {/* Monsters Tab */}
      {activeTab === 'monsters' && (
        <div className="content-table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Monster</th>
                <th>Level</th>
                <th>Stats</th>
                <th>AI</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {monsters.map(monster => (
                <tr key={monster.id} className="content-row">
                  <td className="content-info">
                    <div className="content-name">{monster.name}</div>
                    <div className="content-description">{monster.description}</div>
                    <div className="monster-type">👹 {monster.monsterType}</div>
                  </td>
                  <td className="level-cell">🎯 {monster.level}</td>
                  <td className="monster-stats">
                    <div>❤️ {monster.health} HP</div>
                    <div>⚔️ {monster.damage} ATK</div>
                    <div>🛡️ {monster.defense} DEF</div>
                    <div>💨 {monster.speed} SPD</div>
                  </td>
                  <td>
                    <span className="ai-badge">{monster.aiPersonality}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${monster.isActive ? 'active' : 'inactive'}`}>
                      {monster.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="content-actions">
                    <button 
                      className="view-button"
                      onClick={() => openDetailsModal(monster)}
                    >
                      👁️ View
                    </button>
                    <button 
                      className={`toggle-button ${monster.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus('monsters', monster.id, monster.isActive)}
                    >
                      {monster.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteItem('monsters', monster.id)}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {monsters.length === 0 && (
            <div className="empty-state">
              <h3>No monsters found</h3>
              <p>Create your first monster to get started!</p>
              <button className="create-content-button" onClick={openCreateModal}>
                ✨ Create Monster
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Material Modal */}
      {showCreateModal && activeTab === 'materials' && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✨ Create New Material</h3>
              <button onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Material Name *</label>
                  <input 
                    type="text"
                    value={materialFormData.name}
                    onChange={(e) => setMaterialFormData({...materialFormData, name: e.target.value})}
                    placeholder="e.g., Iron Ore"
                  />
                </div>

                <div className="form-group">
                  <label>Material Type *</label>
                  <select 
                    value={materialFormData.materialType}
                    onChange={(e) => setMaterialFormData({...materialFormData, materialType: e.target.value})}
                  >
                    <option value="Wood">Wood</option>
                    <option value="Ore">Ore</option>
                    <option value="Herbs">Herbs</option>
                    <option value="Gem">Gem</option>
                    <option value="Cloth">Cloth</option>
                    <option value="Leather">Leather</option>
                    <option value="Food">Food</option>
                    <option value="Potion">Potion</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Rarity *</label>
                  <select 
                    value={materialFormData.rarity}
                    onChange={(e) => setMaterialFormData({...materialFormData, rarity: e.target.value})}
                  >
                    <option value="Common">Common</option>
                    <option value="Uncommon">Uncommon</option>
                    <option value="Rare">Rare</option>
                    <option value="Epic">Epic</option>
                    <option value="Legendary">Legendary</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Base Value</label>
                  <input 
                    type="number"
                    min="1"
                    value={materialFormData.baseValue}
                    onChange={(e) => setMaterialFormData({...materialFormData, baseValue: parseInt(e.target.value)})}
                  />
                </div>

                <div className="form-group">
                  <label>Icon Name</label>
                  <input 
                    type="text"
                    value={materialFormData.iconName}
                    onChange={(e) => setMaterialFormData({...materialFormData, iconName: e.target.value})}
                    placeholder="e.g., iron-ore"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input 
                      type="checkbox"
                      checked={materialFormData.isActive}
                      onChange={(e) => setMaterialFormData({...materialFormData, isActive: e.target.checked})}
                    />
                    Active
                  </label>
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea 
                    value={materialFormData.description}
                    onChange={(e) => setMaterialFormData({...materialFormData, description: e.target.value})}
                    placeholder="Describe this material..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button 
                className="create-button"
                onClick={handleCreateMaterial}
                disabled={!materialFormData.name}
              >
                ✨ Create Material
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal - will show based on selected item type */}
      {showDetailsModal && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 {activeTab.charAt(0).toUpperCase() + activeTab.slice(0, -1)} Details</h3>
              <button onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="item-details">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-grid">
                    <div><strong>ID:</strong> {selectedItem.id}</div>
                    <div><strong>Name:</strong> {selectedItem.name}</div>
                    <div><strong>Status:</strong> {selectedItem.isActive ? '✅ Active' : '❌ Inactive'}</div>
                    <div><strong>Created:</strong> {new Date(selectedItem.createdAt).toLocaleString()}</div>
                    <div><strong>Updated:</strong> {new Date(selectedItem.updatedAt).toLocaleString()}</div>
                  </div>
                  <div className="description">
                    <strong>Description:</strong>
                    <p>{selectedItem.description}</p>
                  </div>
                </div>

                {/* Material-specific details */}
                {activeTab === 'materials' && 'rarity' in selectedItem && (
                  <div className="detail-section">
                    <h4>Material Properties</h4>
                    <div className="detail-grid">
                      <div><strong>Type:</strong> {(selectedItem as Material).materialType}</div>
                      <div><strong>Rarity:</strong> <span style={{ color: getRarityColor((selectedItem as Material).rarity) }}>{(selectedItem as Material).rarity}</span></div>
                      <div><strong>Base Value:</strong> 💰 {(selectedItem as Material).baseValue}</div>
                      <div><strong>Icon:</strong> {(selectedItem as Material).iconName}</div>
                    </div>
                  </div>
                )}

                {/* Recipe-specific details */}
                {activeTab === 'recipes' && 'ingredients' in selectedItem && (
                  <>
                    <div className="detail-section">
                      <h4>Recipe Properties</h4>
                      <div className="detail-grid">
                        <div><strong>Category:</strong> {(selectedItem as Recipe).category}</div>
                        <div><strong>Required Level:</strong> 🎯 {(selectedItem as Recipe).requiredLevel}</div>
                        <div><strong>Crafting Time:</strong> ⏱️ {(selectedItem as Recipe).craftingTime}s</div>
                        <div><strong>Base Success Rate:</strong> ✅ {(selectedItem as Recipe).baseSuccessRate}%</div>
                      </div>
                    </div>
                    
                    <div className="detail-section">
                      <h4>Ingredients ({(selectedItem as Recipe).ingredients.length})</h4>
                      <div className="ingredients-list">
                        {(selectedItem as Recipe).ingredients.map((ingredient, index) => (
                          <div key={index} className="ingredient-item">
                            <span className="ingredient-name">{ingredient.materialName}</span>
                            <span className="ingredient-quantity">×{ingredient.quantity}</span>
                            <span 
                              className="ingredient-rarity"
                              style={{ color: getRarityColor(ingredient.rarity) }}
                            >
                              {ingredient.rarity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Monster-specific details */}
                {activeTab === 'monsters' && 'level' in selectedItem && 'health' in selectedItem && (
                  <>
                    <div className="detail-section">
                      <h4>Monster Stats</h4>
                      <div className="detail-grid">
                        <div><strong>Level:</strong> 🎯 {(selectedItem as Monster).level}</div>
                        <div><strong>Type:</strong> {(selectedItem as Monster).monsterType}</div>
                        <div><strong>AI Personality:</strong> {(selectedItem as Monster).aiPersonality}</div>
                      </div>
                      <div className="stats-display">
                        <div className="stat-item">
                          <span className="stat-icon">❤️</span>
                          <span>Health: {(selectedItem as Monster).health}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-icon">⚔️</span>
                          <span>Damage: {(selectedItem as Monster).damage}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-icon">🛡️</span>
                          <span>Defense: {(selectedItem as Monster).defense}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-icon">💨</span>
                          <span>Speed: {(selectedItem as Monster).speed}</span>
                        </div>
                      </div>
                    </div>

                    {(selectedItem as Monster).dropTable && (selectedItem as Monster).dropTable.length > 0 && (
                      <div className="detail-section">
                        <h4>Drop Table ({(selectedItem as Monster).dropTable.length} items)</h4>
                        <div className="drop-table">
                          {(selectedItem as Monster).dropTable.map((drop, index) => (
                            <div key={index} className="drop-item">
                              <span className="drop-material">{drop.materialName}</span>
                              <span className="drop-rate">{drop.dropRate}%</span>
                              <span className="drop-quantity">{drop.minQuantity}-{drop.maxQuantity}x</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="close-button"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentManager;